import { getApiUrl, getHeaders } from '../config/api';
import { getValidToken, handleTokenExpiration } from '../utils/authUtils';

class AdminBookingService {
  constructor() {
    this.baseUrl = getApiUrl('/api/admin');
  }

  // Get authentication token from localStorage with validation
  getAuthToken() {
    const token = getValidToken();
    console.log('Retrieved token:', token ? 'Token exists and valid' : 'No valid token found');
    return token;
  }

  // Fetch all admin bookings from the API
  async fetchAllBookings() {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      console.log('Fetching bookings from:', `${this.baseUrl}/bookings`);
      console.log('Using token:', token.substring(0, 20) + '...');

      const response = await fetch(`${this.baseUrl}/bookings`, {
        method: 'GET',
        headers: getHeaders(token),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.log('Authentication failed, handling token expiration');
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Received data:', data);
      
      // Handle both single booking object and array of bookings
      if (Array.isArray(data)) {
        return data;
      } else if (data && typeof data === 'object') {
        // If it's a single booking object, wrap it in an array
        return [data];
      } else {
        return [];
      }
    } catch (error) {
      console.error('Error fetching admin bookings:', error);
      throw error;
    }
  }

  // Fetch pending bookings from the API
  async fetchPendingBookings() {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      console.log('Fetching pending bookings from:', `${this.baseUrl}/bookings/pending`);
      console.log('Using token:', token.substring(0, 20) + '...');

      const response = await fetch(`${this.baseUrl}/bookings/pending`, {
        method: 'GET',
        headers: getHeaders(token),
      });

      console.log('Pending bookings response status:', response.status);
      console.log('Pending bookings response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.log('Authentication failed while fetching pending bookings, handling token expiration');
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Received pending bookings data:', data);
      
      // Handle both single booking object and array of bookings
      if (Array.isArray(data)) {
        return data;
      } else if (data && typeof data === 'object') {
        // If it's a single booking object, wrap it in an array
        return [data];
      } else {
        return [];
      }
    } catch (error) {
      console.error('Error fetching pending bookings:', error);
      throw error;
    }
  }

  // Fetch confirmed bookings from the API
  async fetchConfirmedBookings() {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      console.log('Fetching confirmed bookings from:', `${this.baseUrl}/bookings/confirmed`);
      console.log('Using token:', token.substring(0, 20) + '...');

      const response = await fetch(`${this.baseUrl}/bookings/confirmed`, {
        method: 'GET',
        headers: getHeaders(token),
      });

      console.log('Confirmed bookings response status:', response.status);
      console.log('Confirmed bookings response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.log('Authentication failed while fetching confirmed bookings, handling token expiration');
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Received confirmed bookings data:', data);
      
      // Handle both single booking object and array of bookings
      if (Array.isArray(data)) {
        return data;
      } else if (data && typeof data === 'object') {
        // If it's a single booking object, wrap it in an array
        return [data];
      } else {
        return [];
      }
    } catch (error) {
      console.error('Error fetching confirmed bookings:', error);
      throw error;
    }
  }

  // Fetch completed bookings from the API
  async fetchCompletedBookings() {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      console.log('Fetching completed bookings from:', `${this.baseUrl}/bookings/complete`);
      console.log('Using token:', token.substring(0, 20) + '...');

      const response = await fetch(`${this.baseUrl}/bookings/complete`, {
        method: 'GET',
        headers: getHeaders(token),
      });

      console.log('Completed bookings response status:', response.status);
      console.log('Completed bookings response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.log('Authentication failed while fetching completed bookings, handling token expiration');
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Received completed bookings data:', data);
      
      // Handle both single booking object and array of bookings
      if (Array.isArray(data)) {
        return data;
      } else if (data && typeof data === 'object') {
        // If it's a single booking object, wrap it in an array
        return [data];
      } else {
        return [];
      }
    } catch (error) {
      console.error('Error fetching completed bookings:', error);
      throw error;
    }
  }

  // Update booking status via API
  async updateBookingStatus(id, status) {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      // Handle different status updates with specific endpoints
      let endpoint;
      if (status === 'in-progress') {
        // Use the specific Pickup endpoint as shown in the API screenshot
        endpoint = `${this.baseUrl}/bookings/${id}/Pickup`;
      } else if (status === 'completed') {
        // Use the specific Complete endpoint as shown in the API screenshot
        endpoint = `${this.baseUrl}/bookings/${id}/complete`;
      } else {
        // Use the general status endpoint for other status updates
        endpoint = `${this.baseUrl}/bookings/${id}/status`;
      }

      console.log(`Updating booking ${id} status to ${status} via:`, endpoint);
      console.log('Using token:', token.substring(0, 20) + '...');

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: getHeaders(token),
        body: (status === 'in-progress' || status === 'completed') ? undefined : JSON.stringify({ status }),
      });

      console.log('Status update response status:', response.status);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.log('Authentication failed during status update, handling token expiration');
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Status update response data:', data);
      return data;
    } catch (error) {
      console.error('Error updating booking status:', error);
      throw error;
    }
  }

  // Confirm booking via API (sends full booking data)
  async confirmBooking(bookingData) {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      console.log('Confirming booking:', bookingData.id);
      console.log('Using token:', token.substring(0, 20) + '...');

      const response = await fetch(`${this.baseUrl}/bookings/${bookingData.id}/confirm`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify(bookingData),
      });

      console.log('Confirm response status:', response.status);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.log('Authentication failed during confirm, handling token expiration');
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Confirm response data:', data);
      return data;
    } catch (error) {
      console.error('Error confirming booking:', error);
      throw error;
    }
  }

  // Get booking by ID
  async getBookingById(id) {
    try {
      const token = this.getAuthToken();
      const response = await fetch(`${this.baseUrl}/bookings/${id}`, {
        method: 'GET',
        headers: getHeaders(token),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching booking by ID:', error);
      throw error;
    }
  }

  // Search bookings by name, mobile, or booking ID using single query parameter
  async searchBookings(searchTerm) {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }
      
      if (!searchTerm || !searchTerm.trim()) {
        // If no search term, return empty array
        return [];
      }

      // Backend now uses single 'query' parameter
      const searchUrl = `${this.baseUrl}/bookings/search?query=${encodeURIComponent(searchTerm.trim())}`;
      
      console.log('Searching with URL:', searchUrl);
      console.log('Using token:', token.substring(0, 20) + '...');

      const response = await fetch(searchUrl, {
        method: 'GET',
        headers: getHeaders(token),
      });

      console.log('Search response status:', response.status);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.log('Authentication failed during search, handling token expiration');
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        if (response.status === 500) {
          // Backend throws RuntimeException for booking not found
          return [];
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Search response data:', data);
      
      // Determine search type for response handling
      const searchType = this.determineSearchType(searchTerm);
      
      // Handle different response formats based on search type
      if (searchType === 'bookingId') {
        // Booking ID search returns a single object, wrap it in an array
        return data ? [data] : [];
      } else {
        // Name and mobile searches return arrays
        return Array.isArray(data) ? data : [];
      }
    } catch (error) {
      console.error('Error searching bookings:', error);
      throw error;
    }
  }

  // Determine search type based on the search term (matches backend logic)
  determineSearchType(searchTerm) {
    if (searchTerm.startsWith('Bk') || searchTerm.startsWith('BK')) {
      return 'bookingId';
    }
    if (/^\d+$/.test(searchTerm)) {
      return 'mobile';
    }
    return 'name';
  }

  // Search bookings by specific parameter (now uses single query parameter)
  async searchBookingsByParam(paramType, value) {
    try {
      const token = this.getAuthToken();
      const response = await fetch(`${this.baseUrl}/bookings/search?query=${encodeURIComponent(value)}`, {
        method: 'GET',
        headers: getHeaders(token),
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied. Please check your authentication.');
        }
        if (response.status === 500) {
          // Backend throws RuntimeException for booking not found
          return [];
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Determine search type for response handling
      const searchType = this.determineSearchType(value);
      
      // Handle different response formats based on search type
      if (searchType === 'bookingId') {
        // Booking ID search returns a single object, wrap it in an array
        return data ? [data] : [];
      } else {
        // Name and mobile searches return arrays
        return Array.isArray(data) ? data : [];
      }
    } catch (error) {
      console.error(`Error searching bookings by ${paramType}:`, error);
      throw error;
    }
  }

  // Get booking statistics
  async getBookingStats() {
    try {
      const token = this.getAuthToken();
      const response = await fetch(`${this.baseUrl}/bookings/stats`, {
        method: 'GET',
        headers: getHeaders(token),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching booking stats:', error);
      // Return default stats if API fails
      return {
        total: 0,
        pending: 0,
        confirmed: 0,
        inProgress: 0,
        completed: 0,
        cancelled: 0
      };
    }
  }

  // Transform API booking data to match frontend format
  transformBookingData(apiBooking) {
    return {
      id: apiBooking.id,
      bookingId: apiBooking.bookingId,
      name: apiBooking.name,
      mobile: apiBooking.mobile,
      address: apiBooking.address,
      landmark: apiBooking.landmark,
      city: apiBooking.city,
      pincode: apiBooking.pincode,
      scrapType: apiBooking.scrapType,
      quantity: apiBooking.quantity,
      photoUrl: apiBooking.photoUrl,
      notes: apiBooking.notes,
      status: this.mapApiStatusToFrontend(apiBooking.status),
      pickupDate: apiBooking.pickupDate,
      pickupSlot: apiBooking.pickupSlot,
      preferredDate: apiBooking.pickupDate, // Map pickupDate to preferredDate for compatibility
      preferredTime: apiBooking.pickupSlot, // Map pickupSlot to preferredTime for compatibility
      createdAt: apiBooking.createdAt,
      updatedAt: apiBooking.updatedAt,
      stepStatus: apiBooking.stepStatus,
      photos: apiBooking.photoUrl ? [{ preview: apiBooking.photoUrl }] : [],
      additionalNotes: apiBooking.notes
    };
  }

  // Map API status to frontend status format
  mapApiStatusToFrontend(apiStatus) {
    const statusMap = {
      'Pending Confirmation': 'pending',
      'Confirmed': 'confirmed',
      'In Progress': 'in-progress',
      'Start Pickup': 'in-progress', // Map "Start Pickup" to "in-progress" for frontend
      'Completed': 'completed',
      'Cancelled': 'cancelled'
    };
    return statusMap[apiStatus] || 'pending';
  }

  // Map frontend status to API status format
  mapFrontendStatusToApi(frontendStatus) {
    const statusMap = {
      'pending': 'Pending Confirmation',
      'confirmed': 'Confirmed',
      'in-progress': 'In Progress',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };
    return statusMap[frontendStatus] || 'Pending Confirmation';
  }
}

export default new AdminBookingService();
